import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Slider from '@react-native-community/slider';
import { Svg, Circle, Text as SvgText, Line, Rect, Path } from 'react-native-svg';
import Footer from '../components/Footer';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobile = screenWidth < 768;

const colors = {
  primary: '#4F46E5',
  background: '#FFFFFF',
  surface: '#F8FAFC',
  cardBackground: '#FFFFFF',
  border: '#E2E8F0',
  text: '#1E293B',
  textSecondary: '#64748B',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#3B82F6',
};

const LumpsumCalculatorScreen = () => {
  const [investmentAmount, setInvestmentAmount] = useState('100000');
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [timePeriod, setTimePeriod] = useState(10);
  const [timePeriodType, setTimePeriodType] = useState('years');
  const [selectedCategory, setSelectedCategory] = useState('equity');
  const [inflationRate, setInflationRate] = useState(6);
  const [taxRate, setTaxRate] = useState(10);
  const [goalAmount, setGoalAmount] = useState('');
  const [isGoalBased, setIsGoalBased] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [results, setResults] = useState(null);

  const investmentCategories = [
    {
      name: 'Large Cap Equity',
      code: 'equity_large',
      expectedReturn: 11,
      riskLevel: 'Moderate',
      description: 'Stable large companies with consistent returns',
      color: '#10B981',
    },
    {
      name: 'Mid Cap Equity',
      code: 'equity_mid',
      expectedReturn: 13,
      riskLevel: 'High',
      description: 'Growing mid-size companies with higher potential',
      color: '#3B82F6',
    },
    {
      name: 'Small Cap Equity',
      code: 'equity_small',
      expectedReturn: 15,
      riskLevel: 'Very High',
      description: 'Small companies with highest growth potential',
      color: '#8B5CF6',
    },
    {
      name: 'Hybrid Funds',
      code: 'hybrid',
      expectedReturn: 10,
      riskLevel: 'Moderate',
      description: 'Balanced mix of equity and debt instruments',
      color: '#F59E0B',
    },
    {
      name: 'Debt Funds',
      code: 'debt',
      expectedReturn: 7,
      riskLevel: 'Low',
      description: 'Conservative debt instruments with stable returns',
      color: '#6B7280',
    },
    {
      name: 'ELSS Funds',
      code: 'elss',
      expectedReturn: 12,
      riskLevel: 'Moderate-High',
      description: 'Tax-saving equity funds with 3-year lock-in',
      color: '#EF4444',
    },
  ];

  const timePeriodOptions = [
    { label: 'Years', value: 'years' },
    { label: 'Months', value: 'months' },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const calculateLumpsum = () => {
    const principal = parseFloat(investmentAmount) || 0;
    let timeInYears = parseFloat(timePeriod) || 0;
    
    // Convert time to years
    if (timePeriodType === 'months') {
      timeInYears = timeInYears / 12;
    }

    // Get expected return rate
    const category = investmentCategories.find(cat => cat.code === selectedCategory);
    const rate = category ? category.expectedReturn : expectedReturn;
    
    // Calculate compound interest: A = P(1 + r/100)^t
    const maturityAmount = principal * Math.pow(1 + rate / 100, timeInYears);
    const totalGains = maturityAmount - principal;

    // Calculate inflation-adjusted returns
    const inflationAdjustedAmount = principal * Math.pow(1 + inflationRate / 100, timeInYears);
    const realMaturityAmount = maturityAmount / Math.pow(1 + inflationRate / 100, timeInYears);
    const realGains = realMaturityAmount - principal;

    // Calculate tax implications
    let taxableGains = 0;
    let taxAmount = 0;
    let postTaxMaturity = maturityAmount;

    if (timeInYears >= 1) {
      // Long Term Capital Gains (LTCG)
      const ltcgExemption = 100000; // ₹1 lakh exemption for equity
      taxableGains = Math.max(0, totalGains - (selectedCategory.includes('equity') ? ltcgExemption : 0));
      const ltcgRate = selectedCategory.includes('equity') ? 10 : 20; // 10% for equity, 20% for debt
      taxAmount = taxableGains * (ltcgRate / 100);
    } else {
      // Short Term Capital Gains (STCG)
      taxableGains = totalGains;
      const stcgRate = selectedCategory.includes('equity') ? 15 : taxRate; // 15% for equity, slab rate for debt
      taxAmount = taxableGains * (stcgRate / 100);
    }

    postTaxMaturity = maturityAmount - taxAmount;
    const postTaxGains = postTaxMaturity - principal;

    // Calculate CAGR (Compound Annual Growth Rate)
    const cagr = ((maturityAmount / principal) ** (1 / timeInYears) - 1) * 100;

    // Calculate goal-based metrics
    let requiredAmount = 0;
    let shortfall = 0;
    let surplus = 0;

    if (isGoalBased && goalAmount) {
      const goal = parseFloat(goalAmount);
      requiredAmount = goal;
      if (maturityAmount >= goal) {
        surplus = maturityAmount - goal;
      } else {
        shortfall = goal - maturityAmount;
      }
    }

    // Calculate year-wise breakdown
    const yearlyBreakdown = [];
    for (let year = 1; year <= Math.min(timeInYears, 20); year++) {
      const yearlyAmount = principal * Math.pow(1 + rate / 100, year);
      const yearlyGains = yearlyAmount - principal;
      const yearlyInflationAdjusted = yearlyAmount / Math.pow(1 + inflationRate / 100, year);
      
      yearlyBreakdown.push({
        year,
        amount: yearlyAmount,
        gains: yearlyGains,
        inflationAdjusted: yearlyInflationAdjusted,
      });
    }

    return {
      principal,
      maturityAmount,
      totalGains,
      rate,
      timeInYears,
      inflationAdjustedAmount,
      realMaturityAmount,
      realGains,
      taxableGains,
      taxAmount,
      postTaxMaturity,
      postTaxGains,
      cagr,
      requiredAmount,
      shortfall,
      surplus,
      yearlyBreakdown,
    };
  };

  useEffect(() => {
    const calculatedResults = calculateLumpsum();
    setResults(calculatedResults);
  }, [
    investmentAmount, expectedReturn, timePeriod, timePeriodType,
    selectedCategory, inflationRate, taxRate, goalAmount, isGoalBased
  ]);

  const renderHeader = () => (
    <View style={styles.header}>
      <Text style={styles.headerTitle}>Lumpsum Investment Calculator</Text>
      <Text style={styles.headerSubtitle}>
        Calculate returns on one-time investments with tax implications, inflation adjustment, and goal planning
      </Text>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorCard}>
      <Text style={styles.cardTitle}>Investment Details</Text>
      
      {/* Investment Amount */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Investment Amount</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={investmentAmount}
            onChangeText={setInvestmentAmount}
            placeholder="100000"
            keyboardType="numeric"
          />
        </View>
      </View>

      {/* Investment Category */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Investment Category</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoryScroll}>
          <View style={styles.categoryContainer}>
            {investmentCategories.map((category) => (
              <TouchableOpacity
                key={category.code}
                style={[
                  styles.categoryCard,
                  selectedCategory === category.code && styles.categoryCardActive,
                ]}
                onPress={() => setSelectedCategory(category.code)}
              >
                <View style={[styles.categoryIndicator, { backgroundColor: category.color }]} />
                <Text style={[
                  styles.categoryName,
                  selectedCategory === category.code && styles.categoryNameActive,
                ]}>
                  {category.name}
                </Text>
                <Text style={[
                  styles.categoryReturn,
                  selectedCategory === category.code && styles.categoryReturnActive,
                ]}>
                  {category.expectedReturn}% p.a.
                </Text>
                <Text style={[
                  styles.categoryRisk,
                  selectedCategory === category.code && styles.categoryRiskActive,
                ]}>
                  {category.riskLevel}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
      </View>

      {/* Expected Return (if custom) */}
      {selectedCategory === 'custom' && (
        <View style={styles.inputGroup}>
          <Text style={styles.label}>Expected Return: {expectedReturn}%</Text>
          <Slider
            style={styles.slider}
            minimumValue={5}
            maximumValue={25}
            value={expectedReturn}
            onValueChange={setExpectedReturn}
            step={0.5}
            minimumTrackTintColor={colors.primary}
            maximumTrackTintColor={colors.border}
            thumbStyle={styles.sliderThumb}
          />
          <View style={styles.sliderLabels}>
            <Text style={styles.sliderLabel}>5%</Text>
            <Text style={styles.sliderLabel}>25%</Text>
          </View>
        </View>
      )}

      {/* Time Period */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Investment Period</Text>
        <View style={styles.timePeriodContainer}>
          <View style={styles.timePeriodInputContainer}>
            <TextInput
              style={styles.timePeriodInput}
              value={timePeriod.toString()}
              onChangeText={(value) => setTimePeriod(parseFloat(value) || 0)}
              placeholder="10"
              keyboardType="numeric"
            />
          </View>
          <View style={styles.timePeriodTypeContainer}>
            {timePeriodOptions.map((option) => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.timePeriodTypeButton,
                  timePeriodType === option.value && styles.timePeriodTypeButtonActive,
                ]}
                onPress={() => setTimePeriodType(option.value)}
              >
                <Text style={[
                  styles.timePeriodTypeText,
                  timePeriodType === option.value && styles.timePeriodTypeTextActive,
                ]}>
                  {option.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>

      {/* Goal-Based Planning */}
      <View style={styles.inputGroup}>
        <TouchableOpacity
          style={styles.checkboxContainer}
          onPress={() => setIsGoalBased(!isGoalBased)}
        >
          <View style={[styles.checkbox, isGoalBased && styles.checkboxActive]}>
            {isGoalBased && (
              <Ionicons name="checkmark" size={16} color={colors.background} />
            )}
          </View>
          <Text style={styles.checkboxLabel}>Goal-Based Planning</Text>
        </TouchableOpacity>
        
        {isGoalBased && (
          <View style={styles.goalInputContainer}>
            <Text style={styles.goalLabel}>Target Amount</Text>
            <View style={styles.inputContainer}>
              <Text style={styles.currencySymbol}>₹</Text>
              <TextInput
                style={styles.textInput}
                value={goalAmount}
                onChangeText={setGoalAmount}
                placeholder="500000"
                keyboardType="numeric"
              />
            </View>
          </View>
        )}
      </View>

      {/* Inflation Rate */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Inflation Rate: {inflationRate}%</Text>
        <Slider
          style={styles.slider}
          minimumValue={3}
          maximumValue={10}
          value={inflationRate}
          onValueChange={setInflationRate}
          step={0.5}
          minimumTrackTintColor={colors.warning}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>3%</Text>
          <Text style={styles.sliderLabel}>10%</Text>
        </View>
      </View>

      {/* Tax Rate */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Tax Rate: {taxRate}%</Text>
        <Slider
          style={styles.slider}
          minimumValue={0}
          maximumValue={30}
          value={taxRate}
          onValueChange={setTaxRate}
          step={5}
          minimumTrackTintColor={colors.error}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>0%</Text>
          <Text style={styles.sliderLabel}>30%</Text>
        </View>
      </View>
    </View>
  );

  const renderDonutChart = () => {
    if (!results) return null;

    const { principal, totalGains, maturityAmount } = results;
    const principalPercentage = (principal / maturityAmount) * 100;
    const gainsPercentage = (totalGains / maturityAmount) * 100;

    const size = 200;
    const strokeWidth = 20;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;

    const principalStrokeDasharray = `${(principalPercentage / 100) * circumference} ${circumference}`;
    const gainsStrokeDasharray = `${(gainsPercentage / 100) * circumference} ${circumference}`;
    const gainsStrokeDashoffset = -((principalPercentage / 100) * circumference);

    return (
      <View style={styles.chartCard}>
        <Text style={styles.cardTitle}>Investment Breakdown</Text>
        <View style={styles.chartContainer}>
          <Svg width={size} height={size}>
            {/* Background circle */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.border}
              strokeWidth={strokeWidth}
              fill="transparent"
            />
            
            {/* Principal amount */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.primary}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={principalStrokeDasharray}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            
            {/* Gains */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.success}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={gainsStrokeDasharray}
              strokeDashoffset={gainsStrokeDashoffset}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            
            {/* Center text */}
            <SvgText
              x={size / 2}
              y={size / 2 - 10}
              textAnchor="middle"
              fontSize="14"
              fontWeight="600"
              fill={colors.text}
            >
              Maturity Value
            </SvgText>
            <SvgText
              x={size / 2}
              y={size / 2 + 10}
              textAnchor="middle"
              fontSize="16"
              fontWeight="bold"
              fill={colors.text}
            >
              {formatCurrency(maturityAmount)}
            </SvgText>
          </Svg>
        </View>
        
        <View style={styles.chartLegend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.primary }]} />
            <Text style={styles.legendText}>Principal ({principalPercentage.toFixed(1)}%)</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.success }]} />
            <Text style={styles.legendText}>Gains ({gainsPercentage.toFixed(1)}%)</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderResults = () => {
    if (!results) return null;

    const { 
      principal,
      maturityAmount, 
      totalGains, 
      rate,
      timeInYears,
      realMaturityAmount,
      realGains,
      taxAmount,
      postTaxMaturity,
      postTaxGains,
      cagr,
      requiredAmount,
      shortfall,
      surplus
    } = results;

    return (
      <View style={styles.resultsCard}>
        <Text style={styles.cardTitle}>Investment Results</Text>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Investment Amount</Text>
          <Text style={styles.resultValue}>{formatCurrency(principal)}</Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Expected Return</Text>
          <Text style={styles.resultValue}>{rate.toFixed(1)}% p.a.</Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Investment Period</Text>
          <Text style={styles.resultValue}>{timeInYears.toFixed(1)} years</Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Maturity Amount</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            {formatCurrency(maturityAmount)}
          </Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Total Gains</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            {formatCurrency(totalGains)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>CAGR</Text>
          <Text style={styles.resultValue}>{cagr.toFixed(2)}%</Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Tax Amount</Text>
          <Text style={[styles.resultValue, { color: colors.error }]}>
            {formatCurrency(taxAmount)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Post-Tax Maturity</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            {formatCurrency(postTaxMaturity)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Real Value (Inflation Adj.)</Text>
          <Text style={[styles.resultValue, { color: colors.info }]}>
            {formatCurrency(realMaturityAmount)}
          </Text>
        </View>

        {isGoalBased && requiredAmount > 0 && (
          <>
            <View style={styles.resultItem}>
              <Text style={styles.resultLabel}>Target Amount</Text>
              <Text style={styles.resultValue}>{formatCurrency(requiredAmount)}</Text>
            </View>
            
            {surplus > 0 ? (
              <View style={styles.resultItem}>
                <Text style={styles.resultLabel}>Surplus</Text>
                <Text style={[styles.resultValue, { color: colors.success }]}>
                  {formatCurrency(surplus)}
                </Text>
              </View>
            ) : shortfall > 0 ? (
              <View style={styles.resultItem}>
                <Text style={styles.resultLabel}>Shortfall</Text>
                <Text style={[styles.resultValue, { color: colors.error }]}>
                  {formatCurrency(shortfall)}
                </Text>
              </View>
            ) : null}
          </>
        )}
      </View>
    );
  };

  const renderGrowthChart = () => {
    if (!results || !results.yearlyBreakdown) return null;

    const { yearlyBreakdown, principal } = results;
    const maxAmount = Math.max(...yearlyBreakdown.map(item => item.amount));
    const chartHeight = 200;
    const chartWidth = 300;
    const padding = 20;

    return (
      <View style={styles.growthCard}>
        <Text style={styles.cardTitle}>Growth Projection</Text>
        <View style={styles.growthContainer}>
          <Svg width={chartWidth} height={chartHeight + padding * 2}>
            {/* Draw line chart */}
            {yearlyBreakdown.slice(0, 15).map((item, index) => {
              const nextItem = yearlyBreakdown[index + 1];
              if (!nextItem) return null;

              const x1 = padding + (index / (yearlyBreakdown.length - 1)) * (chartWidth - padding * 2);
              const y1 = chartHeight + padding - (item.amount / maxAmount) * chartHeight;
              const x2 = padding + ((index + 1) / (yearlyBreakdown.length - 1)) * (chartWidth - padding * 2);
              const y2 = chartHeight + padding - (nextItem.amount / maxAmount) * chartHeight;

              return (
                <Line
                  key={index}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={colors.primary}
                  strokeWidth={2}
                />
              );
            })}

            {/* Draw inflation-adjusted line */}
            {yearlyBreakdown.slice(0, 15).map((item, index) => {
              const nextItem = yearlyBreakdown[index + 1];
              if (!nextItem) return null;

              const x1 = padding + (index / (yearlyBreakdown.length - 1)) * (chartWidth - padding * 2);
              const y1 = chartHeight + padding - (item.inflationAdjusted / maxAmount) * chartHeight;
              const x2 = padding + ((index + 1) / (yearlyBreakdown.length - 1)) * (chartWidth - padding * 2);
              const y2 = chartHeight + padding - (nextItem.inflationAdjusted / maxAmount) * chartHeight;

              return (
                <Line
                  key={`inflation-${index}`}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={colors.warning}
                  strokeWidth={2}
                  strokeDasharray="5,5"
                />
              );
            })}
            
            {/* X-axis */}
            <Line
              x1={padding}
              y1={chartHeight + padding}
              x2={chartWidth - padding}
              y2={chartHeight + padding}
              stroke={colors.border}
              strokeWidth={1}
            />
            
            {/* Y-axis */}
            <Line
              x1={padding}
              y1={padding}
              x2={padding}
              y2={chartHeight + padding}
              stroke={colors.border}
              strokeWidth={1}
            />
          </Svg>
        </View>
        <Text style={styles.chartCaption}>Growth over time (First 15 years)</Text>
        <View style={styles.chartLegend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.primary }]} />
            <Text style={styles.legendText}>Nominal Value</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.warning }]} />
            <Text style={styles.legendText}>Real Value</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderComparison = () => {
    if (!results) return null;

    const { postTaxMaturity, principal, timeInYears } = results;
    
    // Compare with other investment options
    const sipEquivalent = (principal / timeInYears / 12) * ((Math.pow(1 + 12/100/12, timeInYears * 12) - 1) / (12/100/12)) * (1 + 12/100/12);
    const fdReturns = principal * Math.pow(1.065, timeInYears); // 6.5% FD
    const ppfReturns = principal * Math.pow(1.075, timeInYears); // 7.5% PPF
    const goldReturns = principal * Math.pow(1.08, timeInYears); // 8% Gold

    const comparisons = [
      { name: 'Lumpsum Investment', value: postTaxMaturity, returns: results.cagr, color: colors.primary },
      { name: 'Equivalent SIP', value: sipEquivalent, returns: 12, color: colors.success },
      { name: 'Fixed Deposit', value: fdReturns, returns: 6.5, color: colors.warning },
      { name: 'PPF', value: ppfReturns, returns: 7.5, color: colors.info },
      { name: 'Gold Investment', value: goldReturns, returns: 8, color: '#F59E0B' },
    ];

    return (
      <View style={styles.comparisonCard}>
        <TouchableOpacity
          style={styles.cardHeader}
          onPress={() => setShowComparison(!showComparison)}
        >
          <Text style={styles.cardTitle}>Investment Comparison</Text>
          <Ionicons
            name={showComparison ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showComparison && (
          <View style={styles.cardContent}>
            {comparisons.map((item, index) => (
              <View key={index} style={styles.comparisonItem}>
                <View style={styles.comparisonHeader}>
                  <View style={[styles.comparisonIndicator, { backgroundColor: item.color }]} />
                  <Text style={styles.comparisonName}>{item.name}</Text>
                  <Text style={styles.comparisonReturns}>{item.returns.toFixed(1)}%</Text>
                </View>
                <Text style={styles.comparisonValue}>{formatCurrency(item.value)}</Text>
                <View style={styles.comparisonBar}>
                  <View 
                    style={[
                      styles.comparisonBarFill, 
                      { 
                        width: `${(item.value / Math.max(...comparisons.map(c => c.value))) * 100}%`,
                        backgroundColor: item.color 
                      }
                    ]} 
                  />
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  const renderInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.infoTitle}>Complete Guide to Lumpsum Investment Calculator</Text>
      <Text style={styles.infoSubtitle}>Master One-Time Investment Planning with Advanced Tools</Text>
      
      <Text style={styles.infoText}>
        <Text style={{ fontWeight: 'bold' }}>What is Lumpsum Investment?</Text>{'\n'}
        Lumpsum investment is a strategy where you invest a large amount of money at once, rather than spreading it over time through systematic investment plans (SIPs). This approach allows you to deploy capital immediately and benefit from compound growth over the entire investment period. Our lumpsum calculator uses the compound interest formula: A = P(1 + r/n)^(nt), where A is the final amount, P is the principal, r is the annual interest rate, n is the compounding frequency, and t is the time period.
      </Text>
      
      <Text style={styles.infoText}>
        <Text style={{ fontWeight: 'bold' }}>Benefits of Lumpsum Investment:</Text>{'\n'}
        • <Text style={{ fontWeight: '600' }}>Maximum Time in Market:</Text> Your entire capital works from day one, maximizing compound growth potential{'\n'}
        • <Text style={{ fontWeight: '600' }}>Lower Transaction Costs:</Text> Single investment reduces brokerage and transaction fees compared to multiple SIP installments{'\n'}
        • <Text style={{ fontWeight: '600' }}>Immediate Market Exposure:</Text> Benefit from favorable market conditions without waiting for gradual deployment{'\n'}
        • <Text style={{ fontWeight: '600' }}>Simplified Portfolio Management:</Text> Easier to track and manage compared to multiple SIP transactions{'\n'}
        • <Text style={{ fontWeight: '600' }}>Goal-Based Planning:</Text> Ideal for specific financial goals with defined timelines and target amounts
      </Text>
      
      <Text style={styles.infoText}>
        <Text style={{ fontWeight: 'bold' }}>Lumpsum vs SIP Investment Comparison:</Text>{'\n'}
        <Text style={{ fontWeight: '600' }}>Lumpsum Advantages:</Text> Better in rising markets, maximum compound growth, lower costs, immediate full exposure{'\n'}
        <Text style={{ fontWeight: '600' }}>SIP Advantages:</Text> Rupee cost averaging, disciplined investing, better in volatile markets, suitable for regular income earners{'\n'}
        <Text style={{ fontWeight: '600' }}>Best Strategy:</Text> Use lumpsum when markets are reasonably valued and you have surplus funds. Use SIP for regular investments and volatile market conditions.
      </Text>
      
      <Text style={styles.infoText}>
        <Text style={{ fontWeight: 'bold' }}>How to Use Our Lumpsum Calculator:</Text>{'\n'}
        1. <Text style={{ fontWeight: '600' }}>Enter Investment Amount:</Text> Input your one-time investment amount in rupees{'\n'}
        2. <Text style={{ fontWeight: '600' }}>Select Investment Category:</Text> Choose from Large Cap, Mid Cap, Small Cap, Hybrid, or Debt funds based on your risk profile{'\n'}
        3. <Text style={{ fontWeight: '600' }}>Set Time Period:</Text> Define your investment horizon in months or years{'\n'}
        4. <Text style={{ fontWeight: '600' }}>Adjust Parameters:</Text> Fine-tune expected returns, inflation rate, and tax rate for accurate projections{'\n'}
        5. <Text style={{ fontWeight: '600' }}>Goal-Based Planning:</Text> Enable goal-based calculation to determine if your investment meets target requirements
      </Text>
      
      <Text style={styles.infoText}>
        <Text style={{ fontWeight: 'bold' }}>Investment Categories and Expected Returns:</Text>{'\n'}
        • <Text style={{ fontWeight: '600' }}>Large Cap Equity (11% p.a.):</Text> Stable, established companies with consistent performance and lower volatility{'\n'}
        • <Text style={{ fontWeight: '600' }}>Mid Cap Equity (13% p.a.):</Text> Growing companies with higher growth potential and moderate risk{'\n'}
        • <Text style={{ fontWeight: '600' }}>Small Cap Equity (15% p.a.):</Text> High-growth potential companies with higher volatility and risk{'\n'}
        • <Text style={{ fontWeight: '600' }}>Hybrid Funds (9% p.a.):</Text> Balanced allocation between equity and debt for moderate risk-return profile{'\n'}
        • <Text style={{ fontWeight: '600' }}>Debt Funds (7% p.a.):</Text> Conservative investments with stable returns and capital protection
      </Text>
      
      <Text style={styles.infoText}>
        <Text style={{ fontWeight: 'bold' }}>Best Practices for Lumpsum Investment:</Text>{'\n'}
        • <Text style={{ fontWeight: '600' }}>Market Timing:</Text> Invest when markets are at reasonable valuations, avoid investing at market peaks{'\n'}
        • <Text style={{ fontWeight: '600' }}>Diversification:</Text> Spread investments across different asset classes and fund categories{'\n'}
        • <Text style={{ fontWeight: '600' }}>Long-term Approach:</Text> Maintain investment horizon of at least 5-7 years for equity investments{'\n'}
        • <Text style={{ fontWeight: '600' }}>Regular Review:</Text> Monitor portfolio performance and rebalance periodically{'\n'}
        • <Text style={{ fontWeight: '600' }}>Emergency Fund:</Text> Ensure you have adequate emergency funds before making lumpsum investments
      </Text>
      
      <View style={styles.tipBox}>
        <Ionicons name="trending-up" size={16} color={colors.success} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Market Timing Strategy:</Text> Lumpsum investments work best during market corrections or when valuations are reasonable. Consider using systematic transfer plans (STP) to gradually move from debt to equity during volatile periods.
        </Text>
      </View>
      
      <View style={styles.tipBox}>
        <Ionicons name="shield-checkmark" size={16} color={colors.primary} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Tax Optimization:</Text> Long-term equity investments (>1 year) enjoy LTCG tax of 10% on gains above ₹1 lakh. Debt funds held >3 years get indexation benefits. Plan your investment horizon to optimize tax efficiency.
        </Text>
      </View>
      
      <View style={styles.tipBox}>
        <Ionicons name="calculator" size={16} color={colors.warning} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Inflation Protection:</Text> Historical inflation in India averages 6-7%. Aim for real returns (post-inflation) of 3-4% to preserve and grow purchasing power. Equity investments typically provide better inflation protection than debt.
        </Text>
      </View>
      
      <View style={styles.tipBox}>
        <Ionicons name="analytics" size={16} color={colors.info} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>CAGR Understanding:</Text> Compound Annual Growth Rate (CAGR) shows the annual growth rate of your investment. A 12% CAGR means your investment doubles approximately every 6 years through the power of compounding.
        </Text>
      </View>
    </View>
  );

  const renderFAQ = () => {
    const faqData = [
      {
        question: "What is the minimum amount required for lumpsum investment?",
        answer: "Most mutual funds accept lumpsum investments starting from ₹500 to ₹1,000. However, for meaningful wealth creation, consider investing at least ₹10,000 or more. There's no upper limit for lumpsum investments, making it suitable for large corpus deployment."
      },
      {
        question: "How accurate is the lumpsum calculator?",
        answer: "Our lumpsum calculator provides highly accurate projections based on compound interest mathematics. However, actual returns may vary due to market volatility, fund performance, and economic conditions. The calculator assumes consistent returns, while real markets fluctuate. Use it as a planning tool rather than a guarantee."
      },
      {
        question: "When is lumpsum investment better than SIP?",
        answer: "Lumpsum is generally better when: 1) Markets are at reasonable valuations or in correction phase, 2) You have a large surplus amount available, 3) You want maximum time in market for compound growth, 4) Transaction costs need to be minimized. SIP is better for regular income earners, volatile markets, and disciplined investing."
      },
      {
        question: "What are the tax implications of lumpsum investments?",
        answer: "Tax treatment depends on the investment type and holding period: Equity Funds - LTCG (>1 year): 10% on gains above ₹1 lakh, STCG (≤1 year): 15%. Debt Funds - LTCG (>3 years): 20% with indexation, STCG (≤3 years): As per income tax slab. Hybrid funds follow equity taxation if equity allocation >65%."
      },
      {
        question: "How should I choose between different fund categories for lumpsum investment?",
        answer: "Choose based on your risk profile and investment horizon: Large Cap (Low risk, 5+ years) - Stable returns, lower volatility. Mid Cap (Moderate risk, 7+ years) - Higher growth potential. Small Cap (High risk, 10+ years) - Maximum growth potential but volatile. Hybrid (Moderate risk, 3-5 years) - Balanced approach. Debt (Low risk, 1-3 years) - Capital protection with steady returns."
      },
      {
        question: "Should I consider inflation in my lumpsum investment planning?",
        answer: "Absolutely! Inflation erodes purchasing power over time. Historical inflation in India averages 6-7%. Your investments should generate real returns (post-inflation) of 3-4% to maintain wealth. Equity investments typically provide better inflation protection than debt instruments. Our calculator includes inflation adjustment for realistic planning."
      },
      {
        question: "What is the ideal investment horizon for lumpsum investments?",
        answer: "Investment horizon depends on the asset class: Equity funds - Minimum 5-7 years, ideally 10+ years for wealth creation. Debt funds - 1-3 years for stability. Hybrid funds - 3-5 years for balanced growth. Longer horizons allow you to ride out market volatility and benefit from compounding. Never invest money you'll need within 1-2 years in equity."
      },
      {
        question: "Can I make multiple lumpsum investments in the same fund?",
        answer: "Yes, you can make multiple lumpsum investments in the same fund at different times. This strategy, called 'Value Averaging' or 'Systematic Transfer Plan (STP)', helps in rupee cost averaging while maintaining lumpsum benefits. Each investment will have its own holding period for tax calculation purposes."
      },
      {
        question: "How does market timing affect lumpsum investment returns?",
        answer: "Market timing significantly impacts lumpsum returns. Investing at market peaks can lead to lower returns initially, while investing during corrections can enhance returns. However, timing the market consistently is difficult. Consider: 1) Invest when markets are reasonably valued, 2) Use STP from debt to equity during volatile periods, 3) Focus on long-term wealth creation rather than short-term timing."
      },
      {
        question: "What is CAGR and why is it important for lumpsum investments?",
        answer: "CAGR (Compound Annual Growth Rate) represents the annual growth rate of your investment over time. It's crucial for lumpsum investments because: 1) Shows consistent annual returns, 2) Helps compare different investment options, 3) Demonstrates the power of compounding. For example, 12% CAGR doubles your money in ~6 years, while 15% CAGR does it in ~5 years."
      },
      {
        question: "How do I calculate the real returns on my lumpsum investment?",
        answer: "Real returns = Nominal returns - Inflation rate. For example, if your investment generates 12% returns and inflation is 6%, your real return is 6%. This shows the actual purchasing power growth. Our calculator provides both nominal and inflation-adjusted returns to help you understand the real wealth creation potential."
      },
      {
        question: "What are the risks associated with lumpsum investments?",
        answer: "Key risks include: 1) Market Risk - Value fluctuations due to market conditions, 2) Timing Risk - Investing at market peaks, 3) Concentration Risk - Putting all money at once, 4) Liquidity Risk - Difficulty in quick redemption, 5) Inflation Risk - Erosion of purchasing power. Mitigate risks through diversification, proper asset allocation, and long-term approach."
      },
      {
        question: "Should I invest my entire surplus as lumpsum or split it?",
        answer: "Consider a balanced approach: 1) Keep 6-12 months of expenses as emergency fund, 2) Invest 60-70% as lumpsum if markets are reasonably valued, 3) Use remaining 30-40% for SIP or STP over 6-12 months, 4) This strategy provides both immediate market exposure and rupee cost averaging benefits. Adjust based on market conditions and your risk tolerance."
      }
    ];

    return (
      <View style={styles.faqContainer}>
        <TouchableOpacity
          style={styles.faqHeader}
          onPress={() => setShowFAQ(!showFAQ)}
        >
          <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
          <Ionicons
            name={showFAQ ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showFAQ && (
          <View style={styles.faqContent}>
            {faqData.map((faq, index) => (
              <View key={index} style={styles.faqItem}>
                <Text style={styles.faqQuestion}>{faq.question}</Text>
                <Text style={styles.faqAnswer}>{faq.answer}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      
      <View style={styles.content}>
        <View style={styles.leftPanel}>
          {renderCalculator()}
        </View>
        
        <View style={styles.rightPanel}>
          {renderDonutChart()}
          {renderResults()}
          {renderGrowthChart()}
        </View>
      </View>
      
      {renderComparison()}
      {renderInformation()}
      
      <View style={styles.fullWidthSection}>
        {renderFAQ()}
      </View>
      
      <Footer />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    backgroundColor: colors.surface,
    paddingVertical: 40,
    paddingHorizontal: 20,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 8,
    textAlign: 'center',
  },
  headerSubtitle: {
    fontSize: 16,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  content: {
    flexDirection: isMobile ? 'column' : 'row',
    padding: 20,
    gap: 20,
    width: '100%',
  },
  leftPanel: {
    flex: isMobile ? 1 : 1.2,
  },
  rightPanel: {
    flex: 1,
    gap: 20,
  },
  calculatorCard: {
    backgroundColor: colors.cardBackground,
    borderRadius: 12,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 20,
  },
  inputGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 8,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    backgroundColor: colors.background,
  },
  currencySymbol: {
    fontSize: 16,
    color: colors.textSecondary,
    paddingLeft: 12,
  },
  textInput: {
    flex: 1,
    height: 48,
    paddingHorizontal: 12,
    fontSize: 16,
    color: colors.text,
  },
  categoryScroll: {
    marginTop: 8,
  },
  categoryContainer: {
    flexDirection: 'row',
    gap: 12,
    paddingRight: 20,
  },
  categoryCard: {
    backgroundColor: colors.surface,
    borderRadius: 8,
    padding: 12,
    minWidth: 140,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
  },
  categoryCardActive: {
    backgroundColor: colors.primary + '20',
    borderColor: colors.primary,
  },
  categoryIndicator: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginBottom: 8,
  },
  categoryName: {
    fontSize: 12,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 4,
    textAlign: 'center',
  },
  categoryNameActive: {
    color: colors.primary,
  },
  categoryReturn: {
    fontSize: 11,
    color: colors.textSecondary,
    marginBottom: 2,
  },
  categoryReturnActive: {
    color: colors.primary,
  },
  categoryRisk: {
    fontSize: 10,
    color: colors.textSecondary,
  },
  categoryRiskActive: {
    color: colors.primary,
  },
  timePeriodContainer: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
  },
  timePeriodInputContainer: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    backgroundColor: colors.background,
  },
  timePeriodInput: {
    height: 48,
    paddingHorizontal: 12,
    fontSize: 16,
    color: colors.text,
  },
  timePeriodTypeContainer: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    borderRadius: 8,
    padding: 2,
  },
  timePeriodTypeButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  timePeriodTypeButtonActive: {
    backgroundColor: colors.primary,
  },
  timePeriodTypeText: {
    fontSize: 12,
    color: colors.textSecondary,
    fontWeight: '500',
  },
  timePeriodTypeTextActive: {
    color: colors.background,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: colors.border,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  checkboxLabel: {
    fontSize: 14,
    color: colors.text,
    fontWeight: '500',
  },
  goalInputContainer: {
    marginTop: 12,
  },
  goalLabel: {
    fontSize: 12,
    color: colors.textSecondary,
    marginBottom: 6,
  },
  slider: {
    height: 40,
    marginVertical: 10,
  },
  sliderThumb: {
    backgroundColor: colors.primary,
    width: 20,
    height: 20,
  },
  sliderLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  sliderLabel: {
    fontSize: 12,
    color: colors.textSecondary,
  },
  chartCard: {
    backgroundColor: colors.cardBackground,
    borderRadius: 12,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    alignItems: 'center',
  },
  chartContainer: {
    alignItems: 'center',
    marginVertical: 20,
  },
  chartLegend: {
    marginTop: 20,
    gap: 8,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  legendColor: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  legendText: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  resultsCard: {
    backgroundColor: colors.cardBackground,
    borderRadius: 12,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  resultItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  resultLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  resultValue: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
  },
  growthCard: {
    backgroundColor: colors.cardBackground,
    borderRadius: 12,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    alignItems: 'center',
  },
  growthContainer: {
    alignItems: 'center',
    marginVertical: 20,
  },
  chartCaption: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 10,
    textAlign: 'center',
  },
  comparisonCard: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 20,
    margin: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  cardContent: {
    marginTop: 20,
  },
  comparisonItem: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: colors.background,
    borderRadius: 8,
  },
  comparisonHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  comparisonIndicator: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 8,
  },
  comparisonName: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
    flex: 1,
  },
  comparisonReturns: {
    fontSize: 12,
    color: colors.textSecondary,
  },
  comparisonValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 8,
  },
  comparisonBar: {
    height: 4,
    backgroundColor: colors.border,
    borderRadius: 2,
    overflow: 'hidden',
  },
  comparisonBarFill: {
    height: '100%',
    borderRadius: 2,
  },
  infoContainer: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 24,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  infoTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 8,
  },
  infoSubtitle: {
    fontSize: 16,
    color: colors.textSecondary,
    marginBottom: 16,
  },
  infoText: {
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
    marginBottom: 16,
  },
  tipBox: {
    flexDirection: 'row',
    backgroundColor: colors.background,
    borderRadius: 8,
    padding: 12,
    marginTop: 8,
  },
  tipText: {
    fontSize: 13,
    color: colors.textSecondary,
    marginLeft: 8,
    flex: 1,
    lineHeight: 18,
  },
  faqContainer: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  faqHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  faqTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text,
  },
  faqContent: {
    marginTop: 20,
  },
  faqItem: {
    marginBottom: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  faqQuestion: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 8,
  },
  faqAnswer: {
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
  },
  fullWidthSection: {
    paddingHorizontal: 20,
  },
});

export default LumpsumCalculatorScreen;